import { api, apiUrl, resolveTenantSlug, tokens, ApiError } from './api';

/* Mirrors Eduvora.Application.QuestionBank. */

export interface OptionView { label: string; text: string; isCorrect: boolean }

export interface QuestionView {
  id: string;
  subjectId: string;
  subjectName: string;
  unitId: string | null;
  unitName: string | null;
  classLabel: string | null;
  text: string;
  type: string;
  typeLabel: string;
  typology: string;
  typologyLabel: string;
  difficulty: string;
  bloom: string;
  marks: number;
  answerKey: string | null;
  source: string | null;
  origin: string;
  status: string;
  usageCount: number;
  lastUsedOn: string | null;
  hasDiagram: boolean;
  options: OptionView[];
  parts: QuestionView[];
  /** ⚠ False when a case study's sub-parts do not add up. Amber on screen, and the builder
      will not use it. */
  partsAddUp: boolean;
  createdByName: string | null;
  approvedByName: string | null;
}

export interface UnitView {
  id: string; name: string; code: string | null; sequence: number;
  boardMarks: number; approvedQuestions: number; draftQuestions: number;
}

export interface NamedOption { id: string; name: string; code: string }

export interface BlueprintSectionView {
  label: string; title: string; instruction: string | null; sequence: number;
  questionCount: number; marksEach: number; internalChoiceCount: number; allowedTypesCsv: string;
}

export interface BlueprintView {
  id: string; name: string; subjectId: string | null; classLabel: string | null;
  totalMarks: number; durationMinutes: number; generalInstructions: string | null;
  competencyPercent: number; objectivePercent: number; descriptivePercent: number;
  easyPercent: number; mediumPercent: number; hardPercent: number; freshnessDays: number;
  sections: BlueprintSectionView[];
  sectionMarks: number;
  typologyAddsUp: boolean;
}

export interface BankCounts { approved: number; draft: number; retired: number; neverUsed: number }

export interface QuestionPage {
  questions: QuestionView[];
  total: number;
  counts: BankCounts;
  units: UnitView[];
  subjects: NamedOption[];
  blueprints: BlueprintView[];
  institutionId: string | null;
  institutions: NamedOption[];
  note: string | null;
}

// ---------------------------------------------------------------------- health

export interface SectionSupply {
  label: string; title: string; demand: number; approved: number; draft: number;
  marksEach: number; shortfall: number; draftsWouldFix: boolean;
}

export interface SupplyLine {
  name: string; needMarks: number; approvedMarks: number; draftMarks: number;
  shortMarks: number; draftsWouldFix: boolean;
}

export interface BankAction { text: string; urgent: boolean }

export interface BankHealthView {
  blueprintName: string;
  subjectName: string;
  /** "Ready" | "Thin" | "CannotBuild" */
  verdict: string;
  headline: string;
  sections: SectionSupply[];
  typology: SupplyLine[];
  units: SupplyLine[];
  actions: BankAction[];
  approvedTotal: number; draftTotal: number; retiredTotal: number; neverUsed: number;
}

// ---------------------------------------------------------------------- papers

export interface PaperQuestionView {
  itemId: string;
  questionId: string;
  number: number;
  isAlternative: boolean;
  marks: number;
  text: string;
  type: string;
  typeLabel: string;
  typology: string;
  difficulty: string;
  bloom: string;
  unitName: string | null;
  answerKey: string | null;
  source: string | null;
  hasDiagram: boolean;
  options: OptionView[];
  parts: PaperQuestionView[];
  note: string | null;
}

export interface PaperSectionView {
  label: string; title: string; instruction: string | null; marksEach: number;
  questions: PaperQuestionView[];
}

export interface UnfilledView { sectionLabel: string; number: number; marks: number; reason: string }
export interface CoverageView { name: string; targetMarks: number; actualMarks: number; gap: number }

export interface PaperView {
  id: string | null;
  blueprintId: string;
  blueprintName: string;
  subjectId: string;
  subjectName: string;
  /** The school's own name, printed at the top of the paper. */
  institutionName: string;
  title: string;
  classLabel: string | null;
  setLabel: string | null;
  paperDate: string | null;
  totalMarks: number;
  durationMinutes: number;
  generalInstructions: string | null;
  status: string;
  sections: PaperSectionView[];
  unfilled: UnfilledView[];
  typology: CoverageView[];
  units: CoverageView[];
  difficulty: CoverageView[];
  marksPlaced: number;
  questionsPlaced: number;
  repeats: number;
  unitWeightsMissing: boolean;
  note: string | null;
}

export interface PaperSummary {
  id: string; title: string; subjectName: string; classLabel: string | null;
  setLabel: string | null; paperDate: string | null; totalMarks: number;
  status: string; questions: number; createdAt: string;
}

export interface BuildBody {
  blueprintId: string;
  subjectId: string;
  classLabel?: string | null;
  title?: string | null;
  setLabel?: string | null;
  paperDate?: string | null;
  examId?: string | null;
}

export const qbankApi = {
  list: (p: Record<string, string>) =>
    api.get<QuestionPage>(`/api/qbank/questions?${new URLSearchParams(p)}`),

  health: (blueprintId: string, subjectId: string, classLabel?: string) =>
    api.get<BankHealthView>(`/api/qbank/health?${new URLSearchParams({
      blueprintId, subjectId, ...(classLabel ? { classLabel } : {}),
    })}`),

  approve: (id: string) => api.post<{ approved: boolean }>(`/api/qbank/questions/${id}/approve`),
  /** ⚠ Ids, not a filter — the screen approves exactly what it is showing. */
  approveMany: (ids: string[]) =>
    api.post<{ approved: number; skipped: { id: string; reason: string }[] }>(
      '/api/qbank/questions/approve', { ids }),
  retire: (id: string) => api.post<{ retired: boolean }>(`/api/qbank/questions/${id}/retire`),
};

// ---------------------------------------------------------------------- blueprints

/** One row of the section editor on its way OUT. ⚠ Not BlueprintSectionView — that is what comes
    back. This is what a teacher typed, and `sequence` is the order they dragged the rows into. */
export interface BlueprintSectionBody {
  label: string; title: string; instruction?: string | null; sequence: number;
  questionCount: number; marksEach: number; internalChoiceCount: number; allowedTypesCsv: string;
}

export interface BlueprintBody {
  institutionId: string; name: string; subjectId?: string | null; classLabel?: string | null;
  totalMarks: number; durationMinutes: number; generalInstructions?: string | null;
  competencyPercent: number; objectivePercent: number; descriptivePercent: number;
  easyPercent: number; mediumPercent: number; hardPercent: number; freshnessDays: number;
  /** ⚠ Replaces the whole list. The server rebuilds every section from this and resequences 1..n. */
  sections: BlueprintSectionBody[];
}

/**
 * ⚠ ITS OWN FETCH, AND ONLY BECAUSE OF THE ERROR BODY.
 *
 * `api.parse` reads `body.error` for a failed request; every blueprint route answers with
 * `{ message }` carrying the domain's own sentence — "Section B offers an internal choice on 6
 * questions but only has 5". Going through `api.*` would replace that with "Request failed (400)"
 * and the teacher would be left guessing which of six sections is wrong. Written for a human on
 * the server, so it must reach the human on the screen.
 *
 * ⚠ No silent token refresh, unlike `api.*` — `tryRefresh` is private to api.ts. A 401 here shows
 * the sign-in message rather than retrying; the next ordinary call on the page refreshes.
 */
async function blueprintFetch<T>(method: string, path: string, body?: unknown): Promise<T> {
  const slug = resolveTenantSlug();
  const access = tokens.access();
  const res = await fetch(apiUrl(path), {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(slug ? { 'X-Tenant': slug } : {}),
      ...(access ? { Authorization: `Bearer ${access}` } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  const text = await res.text();
  let parsed: unknown = null;
  try { parsed = text ? JSON.parse(text) : null; } catch { /* a proxy error page, not JSON */ }

  if (!res.ok) {
    const m = (parsed as { message?: string; error?: string } | null);
    throw new ApiError(res.status, m?.message ?? m?.error
      ?? (res.status === 401 ? 'Please sign in again.' : `Request failed (${res.status})`));
  }
  return parsed as T;
}

export const blueprintApi = {
  list: (institutionId?: string | null, subjectId?: string | null) =>
    blueprintFetch<BlueprintView[]>('GET', `/api/qbank/blueprints?${new URLSearchParams({
      ...(institutionId ? { institutionId } : {}),
      ...(subjectId ? { subjectId } : {}),
    })}`),

  get: (id: string) => blueprintFetch<BlueprintView>('GET', `/api/qbank/blueprints/${id}`),

  create: (b: BlueprintBody) => blueprintFetch<{ id: string }>('POST', '/api/qbank/blueprints', b),
  update: (id: string, b: BlueprintBody) =>
    blueprintFetch<{ saved: true }>('PUT', `/api/qbank/blueprints/${id}`, b),

  /** ⚠ A blank name is fine — the server names it "<name> (copy)". That is the one-click case. */
  clone: (id: string, name?: string) =>
    blueprintFetch<{ id: string }>('POST', `/api/qbank/blueprints/${id}/clone`, { name: name ?? null }),

  /** ⚠ Behind `hr`, not `teaching` — a Teacher on this screen will get a 403. And it deactivates:
      papers already printed still point at the pattern they were cut from. */
  deactivate: (id: string) =>
    blueprintFetch<{ deactivated: true }>('DELETE', `/api/qbank/blueprints/${id}`),
};

/**
 * The question SHAPES a section may allow — the names are `QuestionType` verbatim.
 *
 * ⚠ THE SPELLING IS LOAD-BEARING AND FAILS LATE. The server parses this CSV against the enum and
 * rejects anything it cannot name; worse, a value that parses but is wrong ("Mcq" typed as
 * "MultipleChoice" and waved through by an older row) gives a section the builder can never fill
 * and a bank-health report that blames the school's question bank for a typo in the pattern. This
 * is why the editor offers checkboxes and never a free-text CSV.
 */
export const QUESTION_TYPES: readonly { name: string; label: string }[] = [
  { name: 'Mcq', label: 'multiple choice' },
  { name: 'AssertionReason', label: 'assertion-reason' },
  { name: 'TrueFalse', label: 'true/false' },
  { name: 'FillInTheBlank', label: 'fill in the blank' },
  { name: 'MatchTheFollowing', label: 'match the following' },
  { name: 'VeryShortAnswer', label: 'very short answer' },
  { name: 'ShortAnswer', label: 'short answer' },
  { name: 'LongAnswer', label: 'long answer' },
  { name: 'CaseStudy', label: 'case study' },
  { name: 'SourceBased', label: 'source-based' },
  { name: 'Map', label: 'map' },
  { name: 'Numerical', label: 'numerical' },
  { name: 'Diagram', label: 'diagram' },
  { name: 'Other', label: 'other' },
];

/** An empty CSV means "anything goes", which is why it round-trips as an empty list, not as all. */
export function parseTypes(csv: string | null | undefined): string[] {
  if (!csv) return [];
  return csv.split(',').map((s) => s.trim()).filter(Boolean);
}

export const paperApi = {
  /* ⚠ Sends the parameters, not the paper. The server runs the builder again — same trust
     boundary as the invigilation chart, and safe for the same reason: it is deterministic. */
  preview: (b: BuildBody) => api.post<PaperView>('/api/papers/preview', b),
  save: (b: BuildBody) => api.post<{ id: string }>('/api/papers', b),
  list: (institutionId?: string) =>
    api.get<PaperSummary[]>(`/api/papers${institutionId ? `?institutionId=${institutionId}` : ''}`),
  get: (id: string) => api.get<PaperView>(`/api/papers/${id}`),
  candidates: (id: string, itemId: string) =>
    api.get<QuestionView[]>(`/api/papers/${id}/swap/${itemId}`),
  swap: (id: string, itemId: string, questionId: string) =>
    api.post<{ swapped: boolean }>(`/api/papers/${id}/swap`, { itemId, questionId }),
  finalise: (id: string) => api.post<{ stamped: number }>(`/api/papers/${id}/finalise`),
};

/** "(i)", "(ii)" — sub-parts of a case study are numbered in lower-case roman, as CBSE prints them. */
export function roman(n: number): string {
  const map: [number, string][] = [[10, 'x'], [9, 'ix'], [5, 'v'], [4, 'iv'], [1, 'i']];
  let out = '';
  let v = n;
  for (const [num, sym] of map) {
    while (v >= num) { out += sym; v -= num; }
  }
  return out;
}

/** Green when the paper is on target, amber when it is close, red when it is not. */
export function gapTone(gap: number): 'ok' | 'warn' | 'bad' {
  const g = Math.abs(gap);
  if (g === 0) return 'ok';
  if (g <= 3) return 'warn';
  return 'bad';
}

// ---------------------------------------------------------------------- the assistant

export interface GapView {
  sectionLabel: string; sectionTitle: string; marks: number;
  type: string; typeLabel: string; typology: string; difficulty: string;
  unitId: string | null; unitName: string; count: number;
  reason: string; label: string;
}

export interface GapListView {
  blueprintName: string; subjectName: string; verdict: string;
  gaps: GapView[]; note: string | null;
}

export interface DraftPartView { marks: number; text: string; answer: string }

export interface DraftView {
  text: string; type: string; typeLabel: string; typology: string;
  difficulty: string; bloom: string; marks: number; answer: string | null;
  options: OptionView[]; parts: DraftPartView[];
  /**
   * Where the assistant says this came from — free text, e.g. "NCERT XII Physics, p. 104".
   *
   * ⚠ Null has TWO meanings and the screen must tell them apart. Written with no `sourceIds`, null
   * simply means "from the model's own knowledge" and is expected. Written FROM chapters, null means
   * the model would not say which page it used, and that draft is the one to distrust.
   *
   * ⚠ It is prose, not an id. Resolving it back to a document and a page is the reader's problem —
   * see resolveCitation() in PaperBuilderPage.
   */
  citation: string | null;
}

export interface RejectedView { text: string; reason: string }

export interface DraftBatchView {
  gapLabel: string;
  drafts: DraftView[];
  rejected: RejectedView[];
  asked: number;
  returned: number;
  savedIds: string[];
  note: string | null;
}

export const assistantApi = {
  gaps: (blueprintId: string, subjectId: string, classLabel?: string) =>
    api.get<GapListView>(`/api/qbank/gaps?${new URLSearchParams({
      blueprintId, subjectId, ...(classLabel ? { classLabel } : {}),
    })}`),

  /* ⚠ `save` defaults to false on the server. Pass it true only when the teacher has said so —
     a preview that quietly writes six rows into the bank is how a bank fills with rubbish. */
  draft: (b: {
    blueprintId: string; subjectId: string; classLabel?: string | null;
    sectionLabel: string; marks: number; type: string; typology: string;
    difficulty: string; unitId?: string | null; count: number; save: boolean;
    /**
     * The chapters to write FROM. ⚠ OMITTED IS NOT THE SAME AS EMPTY-ISH: leave it out and the
     * model writes from its own knowledge, which is a materially different request — different
     * questions, no citations, and no ceiling on how many it will happily invent. Send it only
     * when the teacher has ticked something, and say on screen which of the two is about to run.
     */
    sourceIds?: string[];
  }) => api.post<DraftBatchView>('/api/qbank/draft', b),

  discard: (id: string) => api.del<{ discarded: boolean }>(`/api/qbank/drafts/${id}`),
};

// ---------------------------------------------------------------------- sets

export interface SetSummaryView {
  label: string; mode: string; isReserve: boolean;
  questions: number; marks: number; unfilled: number;
  /* Zero on Set 1 — it prints in the stored order. On every other set this is the number that
     makes it a set rather than a re-print, and the card has to say so. */
  optionsPermuted: number;
  paperId: string | null;
}

export interface EquivalenceLineView {
  axis: string; name: string; marksPerSet: number[]; spread: number;
}

export interface SetPlanView {
  blueprintId: string; blueprintName: string; subjectName: string; mode: string;
  sets: SetSummaryView[];
  setLabels: string[];
  equivalence: EquivalenceLineView[];
  worstSpread: number; worstAxis: string; equivalent: boolean; verdict: string;
  firstSet: PaperView | null;
  note: string | null;
}

export const setsApi = {
  /* ⚠ `save` defaults to false server-side. Building four papers into the database every time
     somebody moves a dropdown is how a school ends up with forty drafts and no idea which is real. */
  build: (b: {
    blueprintId: string; subjectId: string; classLabel?: string | null; title?: string | null;
    paperDate?: string | null; sets: number; mode: 'Shuffled' | 'Parallel';
    withReserve: boolean; save: boolean;
  }) => api.post<SetPlanView>('/api/papers/sets', b),
};


/**
 * How one subject is labelled in a picker.
 *
 * ⚠ THE CODE IS ADDED ONLY WHEN THE NAME IS AMBIGUOUS. A school runs "Art & Craft" for every class
 * it teaches — separate Subject rows, distinct codes, IDENTICAL NAMES. A dropdown that renders only
 * the name shows eleven consecutive lines of "Art & Craft" and invites the reasonable conclusion
 * that the data is duplicated. It is not: the control was hiding the only thing that told them
 * apart, and the code has always been on the wire.
 *
 * ⚠ AND ONLY WHEN AMBIGUOUS. A school with one "Mathematics" gains nothing from reading
 * "MATH10 · Mathematics" — a code is internal vocabulary, and showing it to a teacher who does not
 * need it is its own kind of noise. It appears exactly where it resolves a collision.
 *
 * ⚠ HERE RATHER THAN IN A PAGE, because three pickers need it. A shared label rule that lives inside
 * one screen is how the fourth screen gets it wrong.
 */
export function subjectLabel(s: NamedOption, all: readonly NamedOption[]): string {
  /**
   * ⚠ INNER WHITESPACE IS COLLAPSED, NOT MERELY TRIMMED. "Art & Craft" and "Art  &  Craft" render
   * identically in HTML — the browser collapses runs of spaces — so a reader sees two identical
   * lines while a `trim()` comparison sees two different strings and qualifies neither of them. The
   * comparison has to match what is ON SCREEN, not what is in the column.
   */
  const key = (n: string) => n.trim().toLowerCase().replace(/\s+/g, ' ');
  const sameName = all.filter((x) => key(x.name) === key(s.name));
  if (sameName.length < 2) return s.name;
  /**
   * ⚠ A COLLIDING SUBJECT WITH NO CODE IS LABELLED AS SUCH RATHER THAN LEFT BARE. Falling back to
   * the plain name would leave one unqualified "Art & Craft" sitting among ten qualified ones — and
   * the row that looks different is the row somebody picks, so the single entry we can say least
   * about would collect the mistakes. Saying the code is missing is both true and actionable: it is
   * a master-data gap somebody can go and fill.
   */
  return s.code ? `${s.code} · ${s.name}` : `${s.name} (no code)`;
}
